import { useState, useEffect } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { Search, ShoppingCart, Plus, Minus, X, Grid3x3, List, Loader2, AlertCircle, RefreshCw, ChevronRight, ImageIcon } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { useOrders } from '../../hooks/useOrders';
import { useCategories } from '../../hooks/useCategories';
import { useProducts } from '../../hooks/useProducts';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useTables } from '../../hooks/useTables';
import { useCurrency } from '../../hooks/useCurrency';
import { useMenuTypes } from '../../hooks/useMenuTypes';
import { useAuth } from '../../contexts/AuthContext';

interface MenuItemsPageProps {
  categoryId: string;
  onNavigate?: (page: string) => void;
}

export function MenuItemsPage({ categoryId, onNavigate }: MenuItemsPageProps) {
  // Default to closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(false);
  const [showOnlyPopular, setShowOnlyPopular] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  const { cart, addToCart, removeFromCart, getTotalPrice, getTotalItems } = useCart();
  const { orderByTable, selectedTable } = useRestaurant();
  const { tables } = useTables();
  const { formatCurrency, formatCurrencySpaced } = useCurrency();
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });

  // Get the selected table name
  const selectedTableData = tables.find(table => table.id === selectedTable);
  const tableName = selectedTableData?.name || '';
  
  useEffect(() => {
    console.log('📊 [CustomersPage] Orders Data:', ordersData);
    console.log('📊 [CustomersPage] Orders Count:', ordersData?.length || 0);
  }, [ordersData]);

  const { categories } = useCategories();
  const { products, loading: productsLoading, error: productsError, refresh } = useProducts({ categoryId });
  const { user } = useAuth();
  const { menuTypes } = useMenuTypes(user?.tenantId || user?.restaurantId);

  const category = categories.find(c => c.id === categoryId);
  
  // Get the menu type for the current category
  const currentMenuType = category ? menuTypes.find(mt => mt.id === category.menuTypeId) : null;
  
  // Map backend products to menu items format
  const categoryItems = products.map(product => ({
    id: product.id,
    name: product.name,
    description: product.description || '',
    price: parseFloat(product.price as any) || 0,
    image: product.images && product.images.length > 0 ? product.images[0] : null,
    category: categoryId,
    available: product.isActive,
    popular: product.isFeatured,
  }));

  // Filter items
  const filteredItems = categoryItems.filter(item => {
    const matchesSearch = searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAvailable = !showOnlyAvailable || item.available;
    const matchesPopular = !showOnlyPopular || item.popular;
    return matchesSearch && matchesAvailable && matchesPopular;
  });

  const showInlineCart = cart.length > 0;

  const handleAddItem = (item: any) => {
    addToCart(item);

    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setShowCart(true);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage="menus"
        onNavigate={onNavigate}
        ordersCount={ordersData?.length || 0}
      />
      
      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">
            {/* Breadcrumb Navigation */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm">
                <button
                  onClick={() => onNavigate?.('menuTypes')}
                  className="text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors font-medium"
                >
                  Menus
                </button>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                {currentMenuType && (
                  <>
                    <button
                      onClick={() => {
                        if (currentMenuType) {
                          // Navigate back to the categories page for this menu type
                          localStorage.setItem('selectedMenuTypeId', currentMenuType.id);
                          onNavigate?.('menus');
                        }
                      }}
                      className="text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors font-medium"
                    >
                      {currentMenuType.name}
                    </button>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </>
                )}
                <span className="text-gray-900 dark:text-white font-semibold">
                  {category?.name || 'Menu Items'}
                </span>
              </div>
            </div>

            {/* Page Header with Dropdowns */}
            <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-4xl">
                  {category?.name === 'Appetizers' && '🥗'}
                  {category?.name === 'Main Courses' && '🍽️'}
                  {category?.name === 'Desserts' && '🍰'}
                  {category?.name === 'Beverages' && '🥤'}
                  {category?.name === 'Salads' && '🥗'}
                  {category?.name === 'Burgers' && '🍔'}
                  {category?.name === 'Seafood' && '🦞'}
                  {category?.name === 'Steaks' && '🥩'}
                  {!['Appetizers', 'Main Courses', 'Desserts', 'Beverages', 'Salads', 'Burgers', 'Seafood', 'Steaks'].includes(category?.name || '') && '🍴'}
                </span>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{category?.name || 'Menu Items'}</h1>
                  <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">{category?.description || 'Browse our menu'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Menu Type Dropdown */}
                {menuTypes.length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      Menu Type:
                    </label>
                    <select
                      value={currentMenuType?.id || ''}
                      onChange={(e) => {
                        const selectedMenuTypeId = e.target.value;
                        // Navigate to first category of selected menu type
                        const firstCategory = categories.find(
                          cat => cat.menuTypeId === selectedMenuTypeId && cat.isActive
                        );
                        if (firstCategory) {
                          onNavigate?.(`menu-items-${firstCategory.id}`);
                        }
                      }}
                      className="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium min-w-[150px]"
                    >
                      {menuTypes.filter(mt => mt.isActive).map((menuType) => {
                        const hasCategories = categories.some(cat => cat.menuTypeId === menuType.id && cat.isActive);
                        if (!hasCategories) return null;
                        return (
                          <option key={menuType.id} value={menuType.id}>
                            {menuType.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Category Dropdown */}
                {currentMenuType && categories.filter(cat => cat.menuTypeId === currentMenuType.id && cat.isActive).length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      Category:
                    </label>
                    <select
                      value={categoryId}
                      onChange={(e) => onNavigate?.(`menu-items-${e.target.value}`)}
                      className="px-4 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium min-w-[150px]"
                    >
                      {categories
                        .filter(cat => cat.menuTypeId === currentMenuType.id && cat.isActive)
                        .map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                {/* View Toggle */}
                <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Grid3x3 className="w-4 h-4" />
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      viewMode === 'list'
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    List
                  </button>
                </div>

                {/* Cart Button */}
                <button
                  onClick={() => setShowCart(true)}
                  className="relative flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors lg:hidden"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span className="text-sm font-medium">Cart</span>
                  {getTotalItems() > 0 && (
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {getTotalItems()}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <div className={`grid gap-6 ${showInlineCart ? 'lg:grid-cols-[minmax(0,1fr)_360px]' : 'grid-cols-1'}`}>
              <div className="min-w-0">
                {/* Menu Items Grid/List */}
                {productsLoading ? (
                  <div className="flex items-center justify-center py-24">
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Loading menu items…</p>
                    </div>
                  </div>
                ) : productsError ? (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-8 text-center">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                    <p className="text-sm font-semibold text-red-700 dark:text-red-400 mb-4">{productsError}</p>
                    <button
                      onClick={refresh}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-semibold mx-auto"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Try Again
                    </button>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${showInlineCart ? 'xl:grid-cols-3' : 'lg:grid-cols-3 xl:grid-cols-4'} gap-6`}>
                    {filteredItems.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-md transition-shadow"
                      >
                        <div className="relative">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-48 object-cover cursor-pointer"
                              onClick={() => setSelectedItem(item)}
                            />
                          ) : (
                            <div
                              className="w-full h-48 bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer"
                              onClick={() => setSelectedItem(item)}
                            >
                              <ImageIcon className="w-16 h-16 text-gray-400 dark:text-gray-600" />
                            </div>
                          )}
                          {item.popular && (
                            <div className="absolute top-3 left-3 px-2 py-1 bg-orange-500 text-white text-xs font-semibold rounded-full">
                              Popular
                            </div>
                          )}
                          {!item.available && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="px-4 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg">
                                Out of Stock
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{item.name}</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">{item.description}</p>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(item.price)}</span>
                            {item.available && (
                              <button
                                onClick={() => handleAddItem(item)}
                                className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                              >
                                <Plus className="w-4 h-4" />
                                Add
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredItems.map((item) => (
                      <div
                        key={item.id}
                        className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-4 p-4">
                          <div className="relative flex-shrink-0">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-24 h-24 object-cover rounded-lg cursor-pointer"
                                onClick={() => setSelectedItem(item)}
                              />
                            ) : (
                              <div
                                className="w-24 h-24 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer"
                                onClick={() => setSelectedItem(item)}
                              >
                                <ImageIcon className="w-8 h-8 text-gray-400 dark:text-gray-600" />
                              </div>
                            )}
                            {item.popular && (
                              <div className="absolute top-1 left-1 px-2 py-0.5 bg-orange-500 text-white text-xs font-semibold rounded-full">
                                Popular
                              </div>
                            )}
                            {!item.available && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                                <span className="px-2 py-1 bg-red-500 text-white text-xs font-semibold rounded">
                                  Out of Stock
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-1">{item.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">{item.description}</p>
                            <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(item.price)}</span>
                          </div>
                          {item.available && (
                            <button
                              onClick={() => handleAddItem(item)}
                              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium flex-shrink-0"
                            >
                              <Plus className="w-4 h-4" />
                              Add to Cart
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* No Results — only show after loading is complete */}
                {!productsLoading && !productsError && filteredItems.length === 0 && (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Items Found</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Try adjusting your search or filters
                    </p>
                  </div>
                )}
              </div>

              {showInlineCart && (
                <aside className="hidden lg:block">
                  <div className="sticky top-6 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="border-b border-gray-100 p-5 dark:border-gray-800">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Cart</h2>
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'items'}
                            {orderByTable && tableName ? ` for ${tableName}` : ''}
                          </p>
                        </div>
                        <button
                          onClick={() => onNavigate?.('cart')}
                          className="rounded-lg bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50"
                        >
                          Open Full Cart
                        </button>
                      </div>
                    </div>

                    <div className="max-h-[420px] space-y-3 overflow-y-auto p-5">
                      {cart.map((item) => (
                        <div key={item.id} className="rounded-xl bg-gray-50 p-3 dark:bg-gray-800">
                          <div className="flex items-start gap-3">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="h-16 w-16 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="h-16 w-16 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                                <ImageIcon className="w-6 h-6 text-gray-400 dark:text-gray-600" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{item.name}</h3>
                                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{formatCurrencySpaced(item.price)} each</p>
                                </div>
                                <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{formatCurrencySpaced(item.price * item.quantity)}</p>
                              </div>
                              <div className="mt-3 flex items-center gap-2">
                                <button
                                  onClick={() => removeFromCart(item.id)}
                                  className="rounded-lg border border-gray-200 bg-white p-1.5 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                                >
                                  <Minus className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                                </button>
                                <span className="w-8 text-center text-sm font-semibold text-gray-900 dark:text-white">{item.quantity}</span>
                                <button
                                  onClick={() => handleAddItem(item)}
                                  className="rounded-lg border border-gray-200 bg-white p-1.5 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                                >
                                  <Plus className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="border-t border-gray-100 bg-gray-50 p-5 dark:border-gray-800 dark:bg-gray-800">
                      <div className="mb-2 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                        <span>Items selected</span>
                        <span>{getTotalItems()}</span>
                      </div>
                      {orderByTable && tableName && (
                        <div className="mb-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Table</span>
                          <span className="font-medium text-gray-900 dark:text-white">{tableName}</span>
                        </div>
                      )}
                      <div className="mb-4 flex items-center justify-between border-t border-gray-200 pt-4 text-base font-semibold text-gray-900 dark:border-gray-700 dark:text-white">
                        <span>Total</span>
                        <span className="text-xl text-indigo-600 dark:text-indigo-400">{formatCurrencySpaced(getTotalPrice())}</span>
                      </div>
                      <button
                        onClick={() => onNavigate?.('cart')}
                        className="w-full rounded-lg bg-indigo-600 py-3 font-semibold text-white transition-colors hover:bg-indigo-700"
                      >
                        Review & Send to Kitchen
                      </button>
                    </div>
                  </div>
                </aside>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Cart Sidebar */}
      {showCart && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowCart(false)}
          ></div>
          <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-gray-900 z-50 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {orderByTable && tableName ? `Checkout - ${tableName}` : 'Checkout'}
              </h2>
              <button
                onClick={() => setShowCart(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                          <ImageIcon className="w-7 h-7 text-gray-400 dark:text-gray-600" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{item.name}</h3>
                        <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{formatCurrencySpaced(item.price)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Minus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => handleAddItem(item)}
                          className="p-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Plus className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-800 p-6 bg-gray-50 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                  <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{formatCurrencySpaced(getTotalPrice())}</span>
                </div>
                <button 
                  onClick={() => onNavigate?.('cart')}
                  className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                >
                  🍳 Send to Kitchen
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setSelectedItem(null)}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
              <div className="relative">
                {selectedItem.image ? (
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.name}
                    className="w-full h-64 object-cover"
                  />
                ) : (
                  <div className="w-full h-64 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <ImageIcon className="w-20 h-20 text-gray-400 dark:text-gray-600" />
                  </div>
                )}
                <button
                  onClick={() => setSelectedItem(null)}
                  className="absolute top-4 right-4 p-2 bg-white dark:bg-gray-800 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                {selectedItem.popular && (
                  <div className="absolute top-4 left-4 px-3 py-1.5 bg-orange-500 text-white text-sm font-semibold rounded-full">
                    Popular
                  </div>
                )}
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{selectedItem.name}</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">{selectedItem.description}</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                  <span className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{formatCurrency(selectedItem.price)}</span>
                  {selectedItem.available ? (
                    <button
                      onClick={() => {
                        handleAddItem(selectedItem);
                        setSelectedItem(null);
                      }}
                      className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
                    >
                      <Plus className="w-5 h-5" />
                      Add to Cart
                    </button>
                  ) : (
                    <span className="px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg font-semibold">
                      Out of Stock
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}